// Auth store powered by Clerk
// State is bridged from window.Clerk via onClerkReady() called from App.vue
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToastStore } from './toast'
import router from '@/router'

export const useAuthStore = defineStore('auth', () => {
  const toastStore = useToastStore()

  // Internal reactive state — synced from Clerk via onClerkReady
  const _user = ref(null)
  const _isLoaded = ref(false)

  // ── Computed (matches existing public interface) ───────────────
  const isAuthenticated = computed(() => !!_user.value)
  const isLoading = computed(() => !_isLoaded.value)

  // Expose user for components that need it
  const user = computed(() => _user.value)

  // Legacy-compatible session shape (router guard uses session === null check)
  const session = computed(() => _user.value ? { user: _user.value } : null)

  const userProfile = computed(() => ({
    full_name: _user.value?.fullName || '',
    email: _user.value?.primaryEmailAddress?.emailAddress || '',
    user_type: _user.value?.publicMetadata?.user_type || null,
    roles: _user.value?.publicMetadata?.roles || []
  }))

  const isTalent = computed(() => userProfile.value.user_type === 'talent')
  const isEmployer = computed(() => userProfile.value.user_type === 'employer')

  // ── Clerk bridge ───────────────────────────────────────────────
  // Called once from App.vue once window.Clerk has loaded
  function onClerkReady(clerk) {
    _user.value = clerk.user || null
    _isLoaded.value = true

    clerk.addListener(({ user }) => {
      _user.value = user || null
    })
  }

  // ── Actions ────────────────────────────────────────────────────
  async function initialize() {
    // No-op — Clerk initializes itself via clerkPlugin in main.js
    // onClerkReady() in App.vue completes the bridge
  }

  async function signOut() {
    try {
      if (window.Clerk) {
        await window.Clerk.signOut()
      }
      _user.value = null
      toastStore.info('You have been signed out')
      router.push('/')
      return { success: true }
    } catch (err) {
      console.error('Sign out error:', err)
      toastStore.error('Failed to sign out')
      return { success: false, error: err }
    }
  }

  function hasRole(role) {
    return userProfile.value.roles?.includes(role) || false
  }

  // Expose Clerk's getToken for authenticated API calls
  async function getToken() {
    if (!window.Clerk?.user) return null
    return window.Clerk.session?.getToken()
  }

  return {
    // State
    user,
    session,
    isAuthenticated,
    isLoading,
    userProfile,
    isTalent,
    isEmployer,

    // Actions
    initialize,
    signOut,
    hasRole,
    getToken,
    onClerkReady
  }
})
